import {
  FETCH_PERIODS_FAILURE,
  FETCH_PERIODS_REQUEST,
  FETCH_PERIODS_SUCCESS,
} from '../actions/fetchPeriodsActions';
import { FETCH_UNVALIDATED_PERIODS_SUCCESS } from '../actions/fetchUnvalidatedPeriodsActions';
import {
  ADD_PAGE_DATA,
  UPDATE_APPLICATION_SUCCESS,
  ADD_RESIDENCE_HISTORY,
  REMOVE_RESIDENCE_HISTORY,
  RESET_RESIDENCE_HISTORY,
  PERSIST_RESIDENCE_HISTORY,
  ADD_LIABILITY,
  REMOVE_LIABILITY,
  PERSIST_LIABILITIES,
  WIZARD_PATHS,
  WIZARD_FORWARD,
  WIZARD_BACK,
  WIZARD_UPDATE_PATH,
  WIZARD_UPDATE_FORWARD,
  WIZARD_UPDATE_BACK,
  APPLICATION_SUBMIT_SUCCESS,
  FETCH_CAP_ON_LOAN_SUCCESS,
  FETCH_BUILDER_SUCCESS,
  BUILDER_MERGE_INTO_UI,
  QUERY_ELIGIBILITY_REQUEST,
  QUERY_ELIGIBILITY_SUCCESS,
  QUERY_ELIGIBILITY_ERROR,
  QUERY_MIN_LOAN_AMOUNT_SUCCESS,
  CLEAR_STATE,
  UPDATE_RESIDENCE_HISTORY,
} from '../constants/actionTypes';
import sectionLabels from '../constants/sectionLabels';
import { flattenObject } from '../lib/builderHelpers';
import { fullTimeDateFormat } from '../lib/dateFormatter';
import { roundUpToNearestHundred, roundDownToNearestHundred } from '../lib/helpers';
import wizardConfig from '../lib/wizard/wizardConfig';

/* eslint-disable import/no-cycle */
import clearStateReducer from './clearState';
import eligibilityReducer from './eligibility';
import liabilitiesReducer from './liabilities';
import residenceHistoryReducer from './residenceHistory';
import wizardReducer, { initialState as wizardInitialState } from './wizardProgress';

const { REACT_APP_BUILD_VERSION: BUILD_VERSION } = process.env;

export const initialState = {
  version: BUILD_VERSION,
  wizard: wizardInitialState,
  ui: {
    academicCalendar: 'Semester',
    baseCurrency: '',
    campus: null,
    capOnLoan: { maxLoanAmount: null, futureMaxLoanAmount: null },
    citizenshipCountries: [],
    classId: '',
    countryOfHabitualResidence: null,
    course: null,
    creditCardDebt: { currency: '', amount: '', repaymentMethod: null },
    criminalRecord: null,
    currentLoanPeriod: { amount: '', personalSavings: '', relativesContribution: '', currency: '' },
    currentResidenceStartDate: { month: '', year: '' },
    currentSalary: { currency: '', amount: '' },
    customerGuid: null,
    dateOfBirth: { day: '', month: '', year: '' },
    declarationConfirm: { bankruptOrInsolvent: null },
    deposit: { currency: '', amount: '' },
    employer: null,
    employerCountry: null,
    employmentDates: { startMonth: '', startYear: '', endMonth: '', endYear: '' },
    eligibility: null,
    estimatedAssets: {
      investments: { currency: '', amount: '' },
      property: { currency: '', amount: '' },
      savings: { currency: '', amount: '' },
    },
    everDefaultedLoan: null,
    fullTimeEmployedDuringCourse: false,
    fullTimeEmploymentDates: { startMonth: '', startYear: '' },
    fullTimeJob: null,
    futurePeriods: [],
    futureSalary: { type: '', currency: '', amount: '' },
    gender: '',
    hasConfirmedJobOffer: null,
    hasDebt: null,
    hasPreviousProdigyLoan: null,
    liabilities: [],
    livingExpenses: { currency: '', amount: '' },
    minLoanAmount: 0,
    otherFunds: {
      loans: {
        previous: { currency: '', amount: '' },
        period: { currency: '', amount: '' },
        remainder: { currency: '', amount: '' },
      },
      scholarship: {
        previous: { currency: '', amount: '' },
        period: { currency: '', amount: '' },
        remainder: { currency: '', amount: '' },
      },
      sponsorship: {
        previous: { currency: '', amount: '' },
        period: { currency: '', amount: '' },
        remainder: { currency: '', amount: '' },
      },
    },
    passportName: { firstName: '', lastName: '' },
    period: null,
    periods: {},
    previousEducationFieldOfStudy: '',
    previousEducationLevelOfDegree: '',
    previousEducationUniversity: { name: '', guid: '' },
    previousProdigyLoan: { currency: '', amount: '' },
    privacyPolicy: false,
    programmeName: '',
    programmeLengthEndDate: { month: '', year: '' },
    programmeLengthStartDate: { month: '', year: '' },
    programmeLengthSupportingUrl: '',
    programmeCostsSupportingUrls: [],
    provisionalOffer: {},
    stateOfApplication: 'in_progress',
    readDisclosureDocument: false,
    referralCode: '',
    remainderLoanPeriod: {
      amount: '',
      currency: '',
      personalSavings: '',
      relativesContribution: '',
    },
    residenceCountry: null,
    residenceHistory: [],
    residenceState: null,
    sameResidenceForThreeYears: null,
    school: null,
    studyStartDate: { month: '', year: '' },
    testScore: '',
    testType: '',
    tuitionCost: { currency: '', amount: '' },
    unpaidTuitionCost: { schoolCurrency: '', schoolAmount: '', loanCurrency: '', loanAmount: '' },
    unvalidatedPeriods: [],
    userEmail: '',
    workExperience: { monthsExperience: '', yearsExperience: '' },
    periodsTimeStamp: null,
    periodsLoading: false,
    periodsError: null,
  },
  server: {},
};

const addPageData = (action, state) => {
  const { ui, server, ...otherProps } = state;
  const { uiKey, uiValue } = action.payload;
  let { serverKey, serverValue } = action.payload;

  if (!serverKey && !serverValue) {
    serverKey = uiKey;
    serverValue = uiValue;
  }

  const flattenServerKeys = flattenObject({ [serverKey]: serverValue });

  return {
    ...otherProps,
    ui: {
      ...ui,
      [uiKey]: uiValue,
    },
    server: {
      ...server,
      ...flattenServerKeys,
    },
  };
};

const updateApplicationSuccess = (action, state) => {
  const { server, ...otherProps } = state;
  const { response } = action.payload;

  return {
    ...otherProps,
    server: {
      ...server,
      ...response,
    },
  };
};

const resetResidenceHistory = (action, state) => {
  const { ui, server, ...otherProps } = state;
  return {
    ...otherProps,
    ui: {
      ...ui,
      residenceHistory: residenceHistoryReducer(state.ui.residenceHistory, action),
    },
    server: {
      ...server,
      residenceHistory: residenceHistoryReducer(state.ui.residenceHistory, action),
    },
  };
};

const setWizard = (state, newWizard) => {
  const { ui, server, version } = state;
  return {
    wizard: {
      ...newWizard,
    },
    ui,
    server,
    version,
  };
};

const persistUItoServer = (state, key) => {
  const { ui, server, ...otherProps } = state;
  const uiKey = ui[key];

  return {
    ...otherProps,
    ui,
    server: {
      ...server,
      [key]: [...uiKey],
    },
  };
};

const updateCoursePeriods = (state, payload) => {
  const { ui, ...otherProps } = state;
  const { periods } = ui;
  const { courseGuid, periods: coursePeriods } = payload;
  return {
    ...otherProps,
    ui: {
      ...ui,
      periods: {
        ...periods,
        [courseGuid]: [...coursePeriods],
      },
      periodsTimeStamp: fullTimeDateFormat(Date.now()),
    },
  };
};

const mergeIntoUi = (state, payload) => {
  const { ui, ...otherProps } = state;

  return {
    ...otherProps,
    ui: {
      ...ui,
      ...payload,
    },
  };
};

const mergeIntoServer = (state, payload) => {
  const { server, ...otherProps } = state;

  return {
    ...otherProps,
    server: {
      ...server,
      ...payload,
    },
  };
};

const mergeIntoBuilder = (state, payload) => {
  const { ui, server, ...otherProps } = state;

  return {
    ...otherProps,
    ui: {
      ...ui,
      ...payload,
    },
    server: {
      ...server,
      ...payload,
    },
  };
};

const updateBuilder = payload => {
  const { version = '' } = payload;

  if (version !== BUILD_VERSION) {
    const currentPathObject = wizardConfig.find(path => path.sec === '1');
    return {
      ...payload,
      version: BUILD_VERSION,
      wizard: {
        paths: wizardConfig,
        currentPath: currentPathObject.url,
        currentPathObject,
      },
    };
  }

  return payload;
};

const builder = (state = initialState, action) => {
  const { type, payload } = action;

  let newState;
  switch (type) {
    case ADD_PAGE_DATA:
      newState = addPageData(action, state);
      break;
    case UPDATE_APPLICATION_SUCCESS:
      newState = updateApplicationSuccess(action, state);
      break;
    case APPLICATION_SUBMIT_SUCCESS:
      newState = updateApplicationSuccess(action, state);
      break;
    case RESET_RESIDENCE_HISTORY:
      newState = resetResidenceHistory(action, state);
      break;
    case ADD_RESIDENCE_HISTORY:
    case UPDATE_RESIDENCE_HISTORY:
      const residenceHistory = residenceHistoryReducer(state.ui.residenceHistory, action);
      newState = mergeIntoUi(state, { residenceHistory });
      break;
    case REMOVE_RESIDENCE_HISTORY: {
      const residenceHistory = residenceHistoryReducer(state.ui.residenceHistory, action);
      newState = mergeIntoUi(state, { residenceHistory });
      break;
    }
    case ADD_LIABILITY:
    case REMOVE_LIABILITY: {
      const liabilities = liabilitiesReducer(state.ui.liabilities, action);
      newState = mergeIntoUi(state, { liabilities });
      break;
    }
    case PERSIST_RESIDENCE_HISTORY:
      newState = persistUItoServer(state, 'residenceHistory');
      break;
    case PERSIST_LIABILITIES:
      newState = persistUItoServer(state, 'liabilities');
      break;
    case WIZARD_PATHS:
    case WIZARD_FORWARD:
    case WIZARD_BACK:
    case WIZARD_UPDATE_PATH:
    case WIZARD_UPDATE_FORWARD:
    case WIZARD_UPDATE_BACK: {
      const wizard = wizardReducer(state.wizard, action);
      newState = setWizard(state, wizard);
      if (wizard.currentPathObject) {
        const { name, sec } = wizard.currentPathObject;
        newState = mergeIntoServer(newState, {
          debugPage: name,
          stage: sectionLabels[sec] ? sectionLabels[sec].stage : sectionLabels[sec],
        });
      }
      break;
    }

    case FETCH_PERIODS_REQUEST:
      newState = mergeIntoUi(state, { periodsLoading: true, periodsError: null });
      break;

    case FETCH_PERIODS_SUCCESS:
      newState = updateCoursePeriods(state, payload);
      newState = mergeIntoUi(newState, {
        periodsLoading: false,
        periodsError: null,
      });
      break;

    case FETCH_PERIODS_FAILURE:
      newState = mergeIntoUi(state, { periodsError: payload, periodsLoading: false });
      break;

    case FETCH_UNVALIDATED_PERIODS_SUCCESS:
      newState = updateCoursePeriods(state, payload);
      break;

    case FETCH_CAP_ON_LOAN_SUCCESS: {
      const { maxLoanAmount, futureMaxLoanAmount } = payload;
      const capOnLoan = {
        maxLoanAmount: roundDownToNearestHundred(maxLoanAmount),
        futureMaxLoanAmount: roundDownToNearestHundred(futureMaxLoanAmount),
      };
      newState = mergeIntoUi(state, { capOnLoan });
      newState = mergeIntoServer(newState, { maxLoanAmount });
      newState = mergeIntoServer(newState, { futureMaxLoanAmount });
      break;
    }

    case QUERY_MIN_LOAN_AMOUNT_SUCCESS: {
      const { minimumLoanSize } = payload;
      const minLoanAmount = roundUpToNearestHundred(parseFloat(minimumLoanSize));
      newState = mergeIntoBuilder(state, { minLoanAmount });
      break;
    }

    case FETCH_BUILDER_SUCCESS:
      newState = updateBuilder(payload);
      break;

    case BUILDER_MERGE_INTO_UI:
      newState = mergeIntoUi(state, payload);
      break;

    case QUERY_ELIGIBILITY_REQUEST: {
      newState = mergeIntoBuilder(state, { eligibilityId: null });
      newState = mergeIntoBuilder(newState, { eligibilityStatus: null });
      break;
    }
    case QUERY_ELIGIBILITY_SUCCESS:
    case QUERY_ELIGIBILITY_ERROR: {
      const eligibility = eligibilityReducer(state.ui.eligibility, action);
      newState = mergeIntoUi(state, { eligibility });
      break;
    }
    case CLEAR_STATE:
      newState = clearStateReducer(state, action);
      break;

    default:
      newState = state;
      break;
  }
  return newState;
};

export default builder;
